// ─── Price history for the stock chart (26/10/2026) ──────────────────────────
// The bars behind the chart on the company page, from Yahoo Finance through
// the marketdata function (op=history). One request per symbol and range;
// the answer is kept for as long as the server keeps it.

export const RANGES = [
  { id: "1d",  label: "1D",  span: "היום",                 res: "נר לכל 5 דקות", ttl: 2 * 60 * 1000 },
  { id: "5d",  label: "1W",  span: "בשבוע האחרון",         res: "נר לכל 30 דקות", ttl: 10 * 60 * 1000 },
  { id: "1mo", label: "1M",  span: "בחודש האחרון",         res: "סגירה יומית", ttl: 30 * 60 * 1000 },
  { id: "1y",  label: "1Y",  span: "בשנה האחרונה",         res: "סגירה יומית", ttl: 60 * 60 * 1000 },
  { id: "5y",  label: "5Y",  span: "ב-5 השנים האחרונות",   res: "סגירה שבועית", ttl: 6 * 3600 * 1000 },
  { id: "max", label: "מקס", span: null,                   res: "סגירה חודשית", ttl: 6 * 3600 * 1000 },
];

const memo = new Map();       // `${symbol}|${range}` → { at, data } | { at, promise }

export async function fetchPriceHistory(symbol, range) {
  const k = `${symbol}|${range}`;
  const cfg = RANGES.find(r => r.id === range) || RANGES[2];
  const hit = memo.get(k);
  if (hit && Date.now() - hit.at < cfg.ttl) return hit.promise || hit.data;
  const promise = fetch(`/.netlify/functions/marketdata?op=history&symbol=${encodeURIComponent(symbol)}&range=${range}`)
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null)
    .then(data => {
      if (data && Array.isArray(data.bars) && data.bars.length > 1) memo.set(k, { at: Date.now(), data });
      else memo.delete(k);
      return data && Array.isArray(data.bars) && data.bars.length > 1 ? data : null;
    });
  if (memo.size > 60) memo.delete(memo.keys().next().value);
  memo.set(k, { at: Date.now(), promise });
  return promise;
}

// The change over the range: against yesterday's close on 1D, against the
// range's first close otherwise.
export function rangeChange(history) {
  if (!history || !history.bars?.length) return null;
  const base = history.range === "1d" && history.prevClose > 0 ? history.prevClose : history.bars[0].c;
  const last = history.bars[history.bars.length - 1].c;
  if (!(base > 0) || !(last > 0)) return null;
  return { dollar: last - base, pct: ((last - base) / base) * 100, base, last };
}

// ─── Hebrew dates ────────────────────────────────────────────────────────────
// A bar's time is the exchange's own wall clock: seconds for an intraday bar
// (read as UTC), "YYYY-MM-DD" for a daily one. The chart library hands a
// daily time back as { year, month, day }.
const MONTHS = ["ינו׳", "פבר׳", "מרץ", "אפר׳", "מאי", "יוני", "יולי", "אוג׳", "ספט׳", "אוק׳", "נוב׳", "דצמ׳"];
const DAYS = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

export function timeToDate(time) {
  if (typeof time === "number") return new Date(time * 1000);
  if (typeof time === "string") return new Date(`${time}T00:00:00Z`);
  if (time && typeof time === "object") return new Date(Date.UTC(time.year, time.month - 1, time.day));
  return null;
}

const hhmm = d => `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;

// The label under the axis, by the range on screen.
export function axisLabel(range, time) {
  const d = timeToDate(time);
  if (!d) return "";
  if (range === "1d") return hhmm(d);
  if (range === "5d") return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
  if (range === "1mo") return `${d.getUTCDate()} ב${MONTHS[d.getUTCMonth()]}`;
  if (range === "1y") return `${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  return String(d.getUTCFullYear());
}

// The date over the crosshair.
export function pointLabel(range, time) {
  const d = timeToDate(time);
  if (!d) return "";
  const day = `${d.getUTCDate()} ב${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  if (range === "1d" || range === "5d") return `${day} · ${hhmm(d)}`;
  if (range === "max") return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  if (range === "5y") return `שבוע של ${d.getUTCDate()} ב${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return day;
}

// "מאז 1986" for the whole history: the year of its first bar.
export function spanLabel(range, history) {
  const r = RANGES.find(x => x.id === range);
  if (r?.span) return r.span;
  const d = timeToDate(history?.bars?.[0]?.t);
  return d ? `מאז ${d.getUTCFullYear()}` : "";
}

// "8.4M" for a volume.
export function fmtVolume(v) {
  if (!(v > 0)) return null;
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return String(Math.round(v));
}
